import React, { createContext, useCallback, useContext, useState } from "react";
import { Lock, Check, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TIERS, CASE_PREP, CREDIT_PACK, trialCopy } from "@/lib/plans";

const PaywallContext = createContext({ openPaywall: () => {} });
export const usePaywall = () => useContext(PaywallContext);

function resolveUnlock(unlock) {
  if (unlock === "case_prep") return { ...CASE_PREP, kind: "case_prep" };
  if (unlock === "credits_100") return { name: `${CREDIT_PACK.credits} KeeperCredits`, priceLabel: CREDIT_PACK.priceLabel, price: CREDIT_PACK.price, oneTime: true, kind: "credit_pack", features: ["100 KeeperCredits added instantly", "Never expire — spent after your monthly credits"] };
  const tier = TIERS.find((t) => t.key === unlock);
  return tier ? { ...tier, kind: "subscription" } : null;
}

function Paywall({ config, onClose }) {
  const [busy, setBusy] = useState(false);
  const open = !!config;
  const info = config ? resolveUnlock(config.unlock) : null;
  if (!info) return null;

  const alt = config.altUnlock ? resolveUnlock(config.altUnlock) : null;
  const isTrial = info.kind === "subscription" && info.trial;
  const priceLine = isTrial
    ? trialCopy(info.price, config.trialEnd)
    : info.kind === "subscription"
    ? "Free — no card required"
    : `One-time payment of ${info.priceLabel}. You'll confirm on Stripe's secure checkout.`;

  const confirmLabel = isTrial
    ? `Upgrade to ${info.name}`
    : info.kind === "subscription"
    ? "Continue Free"
    : `Confirm Purchase · ${info.priceLabel}`;

  const startCheckout = async (which) => {
    const target = which === "alt" ? alt : info;
    setBusy(true);
    try {
      if (target.kind === "subscription") {
        if (target.key === "free") { onClose(); toast.success("You're on Basic Watch — free forever."); return; }
        const { data } = await api.post("/subscription/checkout", { plan: target.key, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else if (target.kind === "case_prep") {
        const { data } = await api.post("/billing/case-prep/checkout", { origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      } else if (target.kind === "credit_pack") {
        const { data } = await api.post("/billing/credits/checkout", { pack_id: CREDIT_PACK.key, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const proceed = () => { onClose(); config.onProceed && config.onProceed(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="paywall-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="ek-stamp inline-flex items-center gap-1" style={{ color: "var(--ek-primary)", borderColor: "rgba(46,130,246,0.4)" }}>
              <Lock className="h-3 w-3" /> {info.oneTime ? "ONE-TIME UNLOCK" : "UPGRADE REQUIRED"}
            </span>
          </div>
          <DialogTitle className="font-head font-semibold tracking-tight" data-testid="paywall-feature">
            {config.feature}
          </DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            {config.reason || `Unlock this with ${info.name}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border p-4" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
              <span className="text-sm font-medium" data-testid="paywall-plan-name">{info.name}</span>
            </div>
            <div className="text-right">
              <span className="font-head font-bold text-xl" data-testid="paywall-price">{info.priceLabel}</span>
              {info.kind === "subscription" && info.price > 0 && <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>/mo</span>}
            </div>
          </div>
          <ul className="space-y-1.5 mt-3">
            {(info.features || []).slice(0, 6).map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--ek-text-secondary)" }}>
                <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--ek-primary)" }} /> {f}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs flex items-start gap-1.5" style={{ color: "var(--ek-text-muted)" }} data-testid="paywall-price-line">
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {priceLine}
        </p>

        <div className="flex flex-col gap-2">
          <Button onClick={() => startCheckout("primary")} disabled={busy} data-testid="paywall-confirm-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "Redirecting…" : confirmLabel}
          </Button>
          {alt && (
            <Button onClick={() => startCheckout("alt")} disabled={busy} data-testid="paywall-alt-btn" variant="outline" className="w-full rounded-md border" style={{ borderColor: "rgba(46,130,246,0.4)", background: "transparent", color: "var(--ek-text)" }}>
              {alt.kind === "case_prep" ? `Purchase Case Prep Pass · ${alt.priceLabel}` : `Get ${alt.name}`}
            </Button>
          )}
          {config.onProceed && (
            <button onClick={proceed} disabled={busy} data-testid="paywall-proceed-btn" className="text-sm underline mt-1" style={{ color: "var(--ek-text-secondary)" }}>
              {config.proceedLabel || "Continue without upgrading"}
            </button>
          )}
          <Button onClick={onClose} disabled={busy} data-testid="paywall-cancel-btn" variant="ghost" className="w-full rounded-md" style={{ color: "var(--ek-text-muted)" }}>
            Cancel
          </Button>
        </div>
        <p className="text-[10px] text-center" style={{ color: "var(--ek-text-muted)" }}>
          You are never charged without this confirmation. Payments are processed securely by Stripe.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export function PaywallProvider({ children }) {
  const [config, setConfig] = useState(null);
  const openPaywall = useCallback((cfg) => setConfig(cfg), []);
  return (
    <PaywallContext.Provider value={{ openPaywall }}>
      {children}
      <Paywall config={config} onClose={() => setConfig(null)} />
    </PaywallContext.Provider>
  );
}
